'use client';

import { usePathname } from 'next/navigation';
import { useEffect, useRef } from 'react';

import { emitClientEvent } from '@/lib/analytics/emit-client-event';
import { customerExperienceRollout } from '@/lib/features/customer-experience-rollout';
import {
  getOrCreateClientId,
  rolloutCohortAttribute,
  rolloutSurfaceForPath,
} from '@engine/attribution';

type MetricName = 'LCP' | 'INP' | 'CLS' | 'FCP' | 'TTFB';

function surfaceFor(pathname: string): 'dashboard' | 'public' {
  return pathname.startsWith('/dashboard') ? 'dashboard' : 'public';
}

function deviceClass(): 'mobile' | 'desktop' {
  return typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches
    ? 'mobile'
    : 'desktop';
}

function isInternalBrowser(): boolean {
  try {
    return window.localStorage.getItem('wisprs_internal_opt_out') === '1';
  } catch {
    return false;
  }
}

function rolloutProperties(pathname: string): {
  rollout_group: 'control' | 'treatment';
  rollout_bucket: number;
} {
  const surface = rolloutSurfaceForPath(pathname);
  const cohortAttribute = rolloutCohortAttribute(surface);

  const serverCohort = document
    .querySelector(`[${cohortAttribute}]`)
    ?.getAttribute(cohortAttribute);

  const decision = customerExperienceRollout(
    getOrCreateClientId({ cookieName: 'wisprs_cid', maxAgeSeconds: 60 * 60 * 24 * 365 * 2 }) || 'anonymous',
    { surface },
  );

  if (serverCohort === 'control' || serverCohort === 'treatment') {
    return { rollout_group: serverCohort, rollout_bucket: decision.bucket };
  }

  return { rollout_group: decision.cohort, rollout_bucket: decision.bucket };
}

function ratingFor(metric: MetricName, value: number): 'good' | 'needs-improvement' | 'poor' {
  const [good, poor] =
    metric === 'LCP'
      ? [2500, 4000]
      : metric === 'INP'
        ? [200, 500]
        : metric === 'CLS'
          ? [0.1, 0.25]
          : metric === 'FCP'
            ? [1800, 3000]
            : [800, 1800];

  return value <= good ? 'good' : value <= poor ? 'needs-improvement' : 'poor';
}

function recordMetric(pathname: string, metric: MetricName, value: number) {
  if (!Number.isFinite(value) || value < 0) return;
  emitClientEvent('/api/events/rum', {
    name: 'rum_web_vital',
    route: pathname,
    surface: surfaceFor(pathname),
    metric,
    value_ms: metric === 'CLS' ? value : value,
    rating: ratingFor(metric, value),
    device_class: deviceClass(),
    release:
      process.env.NEXT_PUBLIC_RELEASE_SHA || process.env.NEXT_PUBLIC_APP_VERSION || 'unknown',
    visibility: document.visibilityState,
    ...rolloutProperties(pathname),
  });
}

/**
 * Lightweight, first-party field telemetry for the customer journey. It uses
 * browser PerformanceObserver APIs instead of a runtime dependency and sends
 * only route + numeric timing/category data through the allowlisted RUM route.
 */
export function CustomerRum() {
  const pathname = usePathname();
  const lastPath = useRef<string | null>(null);
  const reportedVitals = useRef(new Set<string>());

  useEffect(() => {
    if (!pathname || isInternalBrowser() || lastPath.current === pathname) return;
    lastPath.current = pathname;

    // SAFETY: The surrounding typed contract establishes the asserted representation.
    const nav = performance.getEntriesByType('navigation')[0] as
      | PerformanceNavigationTiming
      | undefined;

    const startedAt = performance.now();

    const timer = window.setTimeout(() => {
      // SAFETY: The surrounding typed contract establishes the asserted representation.
      const current = performance.getEntriesByType('navigation')[0] as
        | PerformanceNavigationTiming
        | undefined;

      emitClientEvent('/api/events/rum', {
        name: 'rum_navigation',
        route: pathname,
        surface: surfaceFor(pathname),
        navigation_type: current?.type || nav?.type || 'navigate',
        value_ms: Math.max(0, performance.now() - startedAt),
        device_class: deviceClass(),
        release:
          process.env.NEXT_PUBLIC_RELEASE_SHA || process.env.NEXT_PUBLIC_APP_VERSION || 'unknown',
        visibility: document.visibilityState,
        ...rolloutProperties(pathname),
      });

      if (current?.responseStart != null) recordMetric(pathname, 'TTFB', current.responseStart);

      if (current?.duration != null && current.duration > 0) {
        emitClientEvent('/api/events/rum', {
          name: 'rum_navigation',
          route: pathname,
          surface: surfaceFor(pathname),
          navigation_type: current.type || 'navigate',
          value_ms: current.duration,
          metric: 'settled',
          device_class: deviceClass(),
          release:
            process.env.NEXT_PUBLIC_RELEASE_SHA || process.env.NEXT_PUBLIC_APP_VERSION || 'unknown',
          visibility: document.visibilityState,
          ...rolloutProperties(pathname),
        });
      }
    }, 0);

    const observers: PerformanceObserver[] = [];

    const observe = (
      type: string,
      callback: PerformanceObserverCallback,
      options?: PerformanceObserverInit & { durationThreshold?: number }
    ) => {
      if (!('PerformanceObserver' in window)) return;

      try {
        const observer = new PerformanceObserver(callback);
        // SAFETY: The surrounding typed contract establishes the asserted representation.
        observer.observe({ type, buffered: true, ...options } as PerformanceObserverInit);
        observers.push(observer);
      } catch {
        // Some Safari versions reject newer entry types; other metrics continue.
      }
    };

    observe('largest-contentful-paint', (entries) => {
      const entry = entries.getEntries().at(-1);

      if (entry && !reportedVitals.current.has(`${pathname}:LCP`)) {
        reportedVitals.current.add(`${pathname}:LCP`);
        recordMetric(pathname, 'LCP', entry.startTime);
      }
    });
    observe('paint', (entries) => {
      const entry = entries.getEntries().find((item) => item.name === 'first-contentful-paint');

      if (entry && !reportedVitals.current.has(`${pathname}:FCP`)) {
        reportedVitals.current.add(`${pathname}:FCP`);
        recordMetric(pathname, 'FCP', entry.startTime);
      }
    });
    // SAFETY: The surrounding typed contract establishes the asserted representation.
    observe('layout-shift', (entries) => {
      let cls = 0;

      for (const entry of entries.getEntries() as Array<
        PerformanceEntry & { value?: number; hadRecentInput?: boolean }
      >) {
        if (!entry.hadRecentInput) cls += entry.value || 0;
      }

      if (cls > 0 && !reportedVitals.current.has(`${pathname}:CLS`)) {
        reportedVitals.current.add(`${pathname}:CLS`);
        recordMetric(pathname, 'CLS', cls);
      }
    });
    observe(
      'event',
      (entries) => {
        const longest = entries
          .getEntries()
          .reduce((max, entry) => Math.max(max, entry.duration), 0);

        if (longest >= 40 && !reportedVitals.current.has(`${pathname}:INP`)) {
          reportedVitals.current.add(`${pathname}:INP`);
          recordMetric(pathname, 'INP', longest);
        }
      },
      { durationThreshold: 16 }
    );
    observe('longtask', (entries) => {
      const longest = entries.getEntries().reduce((max, entry) => Math.max(max, entry.duration), 0);

      if (longest >= 50) {
        emitClientEvent('/api/events/rum', {
          name: 'rum_long_task',
          route: pathname,
          surface: surfaceFor(pathname),
          value_ms: longest,
          device_class: deviceClass(),
          visibility: document.visibilityState,
          ...rolloutProperties(pathname),
        });
      }
    });

    const onError = () => {
      emitClientEvent('/api/events/rum', {
        name: 'rum_error',
        route: pathname,
        surface: surfaceFor(pathname),
        error_category: 'window_error',
        device_class: deviceClass(),
        ...rolloutProperties(pathname),
      });
    };

    const onRejection = () => {
      emitClientEvent('/api/events/rum', {
        name: 'rum_error',
        route: pathname,
        surface: surfaceFor(pathname),
        error_category: 'unhandled_rejection',
        device_class: deviceClass(),
        ...rolloutProperties(pathname),
      });
    };

    window.addEventListener('error', onError);
    window.addEventListener('unhandledrejection', onRejection);

    return () => {
      window.clearTimeout(timer);
      observers.forEach((observer) => observer.disconnect());
      window.removeEventListener('error', onError);
      window.removeEventListener('unhandledrejection', onRejection);
    };
  }, [pathname]);

  return null;
}
